import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from './PublicLayout';

// Intersection Observer Hook for scroll animations
const useInView = () => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
      }
    }, { threshold: 0.15 });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return [ref, isInView];
};

const Home = () => {
  const [mounted, setMounted] = useState(false);
  const [activeHighlight, setActiveHighlight] = useState(0);
  const [highlightsRef, highlightsInView] = useInView();
  const [workflowRef, workflowInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  useEffect(() => {
    setMounted(true);
  }, []);

  // Auto-rotate highlights every 4 seconds (6 highlights total)
  const HIGHLIGHTS_COUNT = 6;
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveHighlight((prev) => (prev + 1) % HIGHLIGHTS_COUNT);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const highlights = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
        </svg>
      ),
      title: 'Just Describe What You Need',
      desc: 'Tell AI what you want in plain English. No coding, no complex setup. Create powerful prompts in minutes.',
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
        </svg>
      ),
      title: 'AI That Actually Understands',
      desc: 'Get consistent, high-quality results every time. Our tools help you fine-tune AI to work exactly how you want.',
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
        </svg>
      ),
      title: 'Compare Two Models at Once',
      desc: 'Not sure which AI to use? Run two models side-by-side on the same prompt. See quality, speed, and cost compared.',
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
        </svg>
      ),
      title: 'Test Before You Commit',
      desc: 'Try your prompts instantly. See exactly what it costs and how long it takes before using it for real work.',
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
        </svg>
      ),
      title: 'Never Lose Your Best Setup',
      desc: 'Every version is saved automatically. Made a mistake? Go back to what worked with one click.',
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6a7.5 7.5 0 107.5 7.5h-7.5V6z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5H21A7.5 7.5 0 0013.5 3v7.5z" />
        </svg>
      ),
      title: 'Know Exactly What You Spend',
      desc: 'See your AI costs in real-time. No surprise bills. Stay in control of your budget.',
    },
  ];

  const workflow = [
    { step: '1', title: 'Describe', desc: 'Tell us what you want your AI to do' },
    { step: '2', title: 'Create', desc: 'We generate the perfect prompt for you' },
    { step: '3', title: 'Test', desc: 'Try it out and refine until perfect' },
    { step: '4', title: 'Use', desc: 'Deploy via SDK, CLI, or export anywhere' },
  ];

  return (
    <PublicLayout>
      <div className="relative">
        {/* Hero Section */}
        <section className="px-6 lg:px-12 pt-16 lg:pt-24 pb-20 lg:pb-32">
          <div className="max-w-5xl mx-auto text-center">
            {/* Logo */}
            <div 
              className={`flex justify-center mb-8 transition-all duration-1000 ease-out ${
                mounted ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-90'
              }`}
            >
              <img 
                src="/logo.png"
                alt="ForgeVault Logo" 
                className="w-24 h-24 lg:w-32 lg:h-32 object-contain animate-float"
              />
            </div>

            {/* Headline */}
            <h1 
              className={`text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight leading-[1.1] mb-6 text-text-primary transition-all duration-1000 ease-out ${
                mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: '0.1s' }}
            >
              Make AI work
              <br />
              <span className="gradient-text">
                the way you want
              </span>
            </h1>

            {/* Subheadline */}
            <p 
              className={`text-lg sm:text-xl lg:text-2xl leading-relaxed mb-10 max-w-3xl mx-auto text-primary-300/70 transition-all duration-1000 ease-out ${
                mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: '0.2s' }}
            >
              Create AI features that do exactly what you need.
              <br className="hidden sm:block" />
              No coding required. Just describe what you want.
            </p>

            {/* CTA Buttons */}
            <div 
              className={`flex flex-col sm:flex-row gap-4 justify-center mb-8 transition-all duration-1000 ease-out ${
                mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: '0.3s' }}
            >
              <Link
                to="/signup"
                className="group relative px-8 py-4 text-base font-semibold rounded-xl transition-all duration-300 inline-flex items-center justify-center gap-2 overflow-hidden primary-button text-black shadow-glow hover:shadow-glow-lg hover:-translate-y-0.5"
              >
                Start Free — No Credit Card
                <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
              
              <Link
                to="/features"
                className="px-8 py-4 text-base font-medium rounded-xl transition-all duration-300 inline-flex items-center justify-center gap-2 bg-primary-500/8 border border-primary-500/20 text-primary-300/90 hover:bg-primary-500/15 hover:border-primary-500/30"
              >
                See How It Works
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
            </div>

            {/* Trust Signals */}
            <div 
              className={`flex flex-col items-center gap-4 transition-all duration-1000 ease-out ${
                mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: '0.4s' }}
            >
              <p className="text-sm text-primary-300/50">Powered by the world's best AI</p>
              <div className="flex flex-wrap justify-center gap-6 items-center opacity-60">
                <span className="text-sm font-medium text-primary-300/70">ChatGPT</span>
                <span className="text-primary-500/30">•</span>
                <span className="text-sm font-medium text-primary-300/70">Claude</span>
                <span className="text-primary-500/30">•</span>
                <span className="text-sm font-medium text-primary-300/70">Llama</span>
                </div>
            </div>

          </div>
        </section>

        {/* Divider */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="h-px divider-gradient" />
        </div>

        {/* Feature Highlights - Auto rotating */}
        <section 
          ref={highlightsRef}
          className="px-6 lg:px-12 py-20 lg:py-28"
        >
          <div className="max-w-5xl mx-auto">
            <h2 
              className={`text-2xl lg:text-4xl font-bold mb-4 text-center text-text-primary transition-all duration-600 ease-out ${
                highlightsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
              }`}
            >
              Everything You Need to Harness AI
            </h2>
            <p 
              className={`text-center mb-12 max-w-2xl mx-auto text-primary-300/60 transition-all duration-600 ease-out ${
                highlightsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
              }`}
              style={{ transitionDelay: '0.1s' }}
            >
              For business owners, creators, and developers — build in the UI, deploy via SDK.
            </p>

            {/* Active Highlight Card */}
            <div className="max-w-2xl mx-auto mb-10 p-8 lg:p-10 rounded-2xl text-center highlight-card border border-primary-500/15 shadow-2xl">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-5 bg-success-50 text-primary-500">
                {highlights[activeHighlight]?.icon}
              </div>
              <h3 className="text-xl lg:text-2xl font-bold mb-3 text-text-primary">
                {highlights[activeHighlight]?.title}
              </h3>
              <p className="text-base lg:text-lg leading-relaxed text-primary-300/75">
                {highlights[activeHighlight]?.desc}
              </p>
            </div>

            {/* Dot Indicators */}
            <div className="flex justify-center gap-2">
              {highlights.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveHighlight(idx)}
                  className="p-1 transition-all duration-300"
                  aria-label={`Feature ${idx + 1}`}
                >
                  <div 
                    className={`rounded-full transition-all duration-300 ${
                      idx === activeHighlight 
                        ? 'w-6 h-2 indicator-active shadow-glow-sm' 
                        : 'w-2 h-2 bg-primary-500/25'
                    }`}
                  />
                </button>
              ))}
            </div>

            {/* See All Features Link */}
            <div className="text-center mt-8">
              <Link
                to="/features"
                className="inline-flex items-center gap-2 text-sm font-medium transition-all duration-300 text-primary-300/70 hover:text-primary-500"
              >
                See all features in detail
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="h-px divider-gradient" />
        </div>

        {/* Workflow Section */}
        <section 
          ref={workflowRef}
          className="px-6 lg:px-12 py-20 lg:py-28"
        >
          <div className="max-w-5xl mx-auto">
            <h2 
              className={`text-2xl lg:text-4xl font-bold mb-4 text-center text-text-primary transition-all duration-600 ease-out ${
                workflowInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
              }`}
            >
              Get Started in Minutes
            </h2>
            <p 
              className={`text-center mb-12 max-w-2xl mx-auto text-primary-300/60 transition-all duration-600 ease-out ${
                workflowInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
              }`}
              style={{ transitionDelay: '0.1s' }}
            >
              Four simple steps to your first AI feature
            </p>

            <div className="flex flex-wrap justify-center gap-4 lg:gap-6">
              {workflow.map((item, idx) => (
                <div
                  key={idx}
                  className={`flex items-center gap-4 transition-all duration-500 ease-out ${
                    workflowInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-5'
                  }`}
                  style={{ transitionDelay: `${0.1 * idx}s` }}
                >
                  <div className="flex items-center gap-3 px-5 py-4 rounded-xl workflow-card border border-primary-500/15">
                    <span className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold primary-button text-black">
                      {item.step}
                    </span>
                    <div>
                      <div className="font-semibold text-sm text-text-primary">{item.title}</div>
                      <div className="text-xs text-primary-300/50">{item.desc}</div>
                    </div>
                  </div>
                  {idx < workflow.length - 1 && (
                    <svg className="w-5 h-5 hidden lg:block text-primary-500/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="h-px divider-gradient" />
        </div>

        {/* CTA Section */}
        <section ref={ctaRef} className="px-6 lg:px-12 py-20 lg:py-28">
          <div className="max-w-4xl mx-auto">
            <div 
              className={`relative p-10 lg:p-16 rounded-3xl overflow-hidden text-center cta-card border border-primary-500/15 transition-all duration-1000 ease-out ${
                ctaInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              {/* Background glow */}
              <div className="absolute inset-0 pointer-events-none cta-glow" />

              <div className="relative z-10">
                <h2 className="text-3xl lg:text-5xl font-bold mb-4 text-text-primary">
                  Ready to try it yourself?
                </h2>
                <p className="text-lg mb-10 max-w-xl mx-auto text-primary-300/60">
                  Start free, no credit card needed. Create your first AI prompt in under 5 minutes.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <Link
                    to="/signup"
                    className="group inline-flex items-center gap-2 px-10 py-5 text-lg font-semibold rounded-xl transition-all duration-300 primary-button text-black shadow-glow hover:shadow-glow-lg hover:-translate-y-1"
                  >
                    Start Free Now
                    <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </Link>
                  <a 
                    href="https://pypi.org/project/forgevault/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-primary-300/70 hover:text-primary-500 transition-colors"
                  >
                    <code className="text-sm bg-primary-500/10 px-3 py-2 rounded-lg font-mono">pip install forgevault</code>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <style>{`
        .gradient-text {
          background-image: linear-gradient(135deg, #32CD32 0%, #98FB98 50%, #32CD32 100%);
          background-size: 200% auto;
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: gradientFlow 3s linear infinite;
        }
        .primary-button {
          background: linear-gradient(135deg, #228B22 0%, #32CD32 100%);
        }
        .divider-gradient {
          background: linear-gradient(90deg, transparent 0%, rgba(50, 205, 50, 0.2) 50%, transparent 100%);
        }
        .highlight-card {
          background: rgba(10, 18, 10, 0.8);
        }
        .workflow-card {
          background: rgba(10, 18, 10, 0.6);
        }
        .cta-card {
          background: linear-gradient(135deg, rgba(50, 205, 50, 0.1) 0%, rgba(34, 139, 34, 0.05) 100%);
        }
        .cta-glow {
          background: radial-gradient(ellipse at 50% 0%, rgba(50, 205, 50, 0.15) 0%, transparent 60%);
        }
        .indicator-active {
          background: linear-gradient(90deg, #228B22, #32CD32);
        }
        @keyframes gradientFlow {
          0% { background-position: 0% center; }
          100% { background-position: 200% center; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
      `}</style>
    </PublicLayout>
  );
};

export default Home;

